import { BookOpen, Check, GraduationCap, Pencil, Trash } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { LessonPlannerV2ResponseDto } from "@packages/api/madlenAPI.schemas";
import { Button } from "@core/button";
import { CourseData, GradeOptions } from "@packages/constant/CourseData";

interface ColorTheme {
  card: {
    background: string;
    border: string;
    hoverTransform: string;
  };
  header: {
    background: string;
  };
  button: {
    background: string;
    hover: string;
  };
  tag: {
    background: string;
    border: string;
    text: string;
  };
}

const colorThemes: Record<"default" | "maarif" | "ubd", ColorTheme> = {
  default: {
    card: {
      background: "bg-white dark:bg-gray-800",
      border: "border-gray-100 dark:border-gray-700",
      hoverTransform: "hover:-translate-y-1",
    },
    header: {
      background:
        "bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-900/10 dark:to-green-900/10",
    },
    button: {
      background: "from-emerald-500 to-green-500",
      hover: "hover:from-emerald-600 hover:to-green-600",
    },
    tag: {
      background: "bg-emerald-50 dark:bg-emerald-900/20",
      border: "border-emerald-100 dark:border-emerald-800",
      text: "text-emerald-800 dark:text-emerald-200",
    },
  },
  maarif: {
    card: {
      background: "bg-white dark:bg-gray-800",
      border: "border-red-100 dark:border-red-800",
      hoverTransform: "hover:-translate-y-1",
    },
    header: {
      background:
        "bg-gradient-to-r from-red-50 to-rose-50 dark:from-red-900/10 dark:to-rose-900/10",
    },
    button: {
      background: "from-red-500 to-rose-500",
      hover: "hover:from-red-600 hover:to-rose-600",
    },
    tag: {
      background: "bg-red-50 dark:bg-red-900/20",
      border: "border-red-100 dark:border-red-800",
      text: "text-red-800 dark:text-red-200",
    },
  },
  ubd: {
    card: {
      background: "bg-white dark:bg-gray-800",
      border: "border-purple-100 dark:border-purple-800",
      hoverTransform: "hover:-translate-y-1",
    },
    header: {
      background:
        "bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/10 dark:to-indigo-900/10",
    },
    button: {
      background: "from-purple-600 to-indigo-600",
      hover: "hover:from-purple-700 hover:to-indigo-700",
    },
    tag: {
      background: "bg-purple-50 dark:bg-purple-900/20",
      border: "border-purple-100 dark:border-purple-800",
      text: "text-purple-800 dark:text-purple-200",
    },
  },
};

interface PastLessonPlanCardProps {
  plan?: LessonPlannerV2ResponseDto;
  onViewDetails: (planId: number) => void;
  onDelete?: (planId: number) => void;
  onUpdateTitle?: (planId: number, newTitle: string) => void;
  colorTheme?: "default" | "maarif" | "ubd";
}

export const PastLessonPlanCard = ({
  plan,
  onViewDetails,
  onDelete,
  onUpdateTitle,
  colorTheme = "default",
}: PastLessonPlanCardProps) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState(plan?.name ?? "");
  const title = plan?.name ?? t(`lessonPlanner.unknownTitle`, { id: plan?.id });
  const theme = colorThemes[colorTheme];

  const handleEditClick = () => {
    setEditedTitle(title);
    setIsEditing(true);
  };

  const handleSaveTitle = () => {
    if (plan?.id && onUpdateTitle) {
      onUpdateTitle(plan.id, editedTitle);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSaveTitle();
    } else if (e.key === "Escape") {
      setIsEditing(false);
      setEditedTitle(title);
    }
  };

  return (
    <div
      className={`group ${theme.card.background} rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 transform ${theme.card.hoverTransform} overflow-hidden border ${theme.card.border}`}
    >
      <div className="flex flex-col h-full">
        {/* Header with gradient overlay */}
        <div className={`relative ${theme.header.background} p-4 pb-3`}>
          {/* Title Section */}
          <div className="min-w-0 w-full mb-2">
            {isEditing ? (
              <div className="flex gap-2">
                <input
                  type="text"
                  value={editedTitle}
                  onChange={(e) => setEditedTitle(e.target.value)}
                  onBlur={handleSaveTitle}
                  onKeyDown={handleKeyDown}
                  className="w-full px-2 py-1.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 text-sm"
                  autoFocus
                  maxLength={100}
                />
                <Button
                  onClick={handleSaveTitle}
                  size="sm"
                  variant="solid"
                  rounded="lg"
                  bgColor="bg-orange-500 hover:bg-orange-600"
                  className="text-white"
                  leftIcon={<Check size={14} />}
                />
              </div>
            ) : (
              <div className="flex items-start justify-between gap-2">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white break-words line-clamp-2 flex-1">
                  {title}
                </h3>
                <div className="flex items-start gap-1">
                  <button
                    onClick={handleEditClick}
                    className="p-1 hover:bg-white/80 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    title={t("lessonPlanner.actions.edit")}
                  >
                    <Pencil
                      size={14}
                      className="text-gray-500 dark:text-gray-400"
                    />
                  </button>
                  {onDelete && (
                    <button
                      onClick={() => plan?.id && onDelete(plan.id)}
                      className="p-1 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                      title={t("lessonPlanner.actions.delete")}
                    >
                      <Trash
                        size={14}
                        className="text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400"
                      />
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Metadata Pills */}
          <div className="flex flex-wrap gap-1.5 mb-2">
            {plan?.grade && (
              <div className="inline-flex items-center px-2 py-0.5 rounded-lg bg-gray-100 dark:bg-gray-700/50 text-xs text-gray-700 dark:text-gray-300">
                <GraduationCap size={12} className="mr-1" />
                <span>
                  {
                    GradeOptions(t).find(
                      (a) => a.value == plan?.grade?.toString(),
                    )?.label
                  }
                  {plan?.grade > 0 && ". Sınıf"}
                </span>
              </div>
            )}
            {plan?.courseType && (
              <div className="inline-flex items-center px-2 py-0.5 rounded-lg bg-gray-100 dark:bg-gray-700/50 text-xs text-gray-700 dark:text-gray-300">
                <BookOpen size={12} className="mr-1" />
                <span>
                  {t(
                    "courseType." +
                      CourseData[plan.courseType]?.name(plan.grade ?? 8),
                  )}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6">
          <Button
            onClick={() => onViewDetails(plan?.id ?? 0)}
            className={`bg-gradient-to-r ${theme.button.background} ${theme.button.hover} hover:shadow-md`}
            bgColor=""
            textColor="text-white"
            rounded="lg"
            fullWidth
            size="md"
          >
            {t("lessonPlanner.viewDetails")}
          </Button>
        </div>
      </div>
    </div>
  );
};
